import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'cobstream:profile';

export interface LocalProfile {
  localId: string;
  displayName: string | null;
}

/**
 * A stable per-browser identity for anonymous users. The localId seeds the
 * guest JWT's `sub` so the same browser keeps the same identity across
 * lobby reconnects. displayName is what the lobby chat broadcasts as the
 * sender name when no real account is signed in.
 */
function genLocalId(): string {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 16);
  }
  return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
}

function readProfile(): LocalProfile {
  if (typeof window === 'undefined') {
    return { localId: 'ssr', displayName: null };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LocalProfile>;
      if (typeof parsed.localId === 'string' && parsed.localId.length > 0) {
        return {
          localId: parsed.localId,
          displayName: typeof parsed.displayName === 'string' ? parsed.displayName : null,
        };
      }
    }
  } catch {
    // corrupt JSON — fall through and re-init
  }
  const fresh: LocalProfile = { localId: genLocalId(), displayName: null };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
  return fresh;
}

function writeProfile(profile: LocalProfile): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}

export interface UseLocalProfile extends LocalProfile {
  setDisplayName: (next: string | null) => void;
}

export function useLocalProfile(): UseLocalProfile {
  const [profile, setProfile] = useState<LocalProfile>(() => readProfile());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onStorage(e: StorageEvent): void {
      if (e.key === STORAGE_KEY) setProfile(readProfile());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setDisplayName = useCallback((next: string | null): void => {
    setProfile((prev) => {
      const trimmed = (next ?? '').trim();
      const updated: LocalProfile = { ...prev, displayName: trimmed.length ? trimmed : null };
      writeProfile(updated);
      return updated;
    });
  }, []);

  return { ...profile, setDisplayName };
}
